import React, { useEffect, useRef, useState, useLayoutEffect } from 'react';
import {
  LuBot,
  LuCheck,
  LuCheckCheck,
  LuClock,
  LuCircleAlert,
  LuX,
  LuDownload,
  LuImage,
  LuChevronDown,
  LuFile,
  LuImageOff,
  LuVideoOff,
  LuBan,
  LuHandshake,
  LuExternalLink,
} from 'react-icons/lu';
import { FaFacebook, FaInstagram } from 'react-icons/fa';
import './ConversationWindow.css';
import AudioPlayer from './AudioPlayer';
import type { Message } from '../data/mockData';
import {
  capaDoAnuncio,
  linkDoAnuncio,
  nomeDoAnuncio,
  textoDoAnuncio,
  rotuloRedeDoAnuncio,
  type AtribuicaoAnuncio,
} from '../services/TrafegoPagoService';

interface ConversationWindowProps {
  messagesByDate: { [date: string]: Message[] };
  conversationId: number;
  /** Nome do agente de IA, para assinar as mensagens que ela escreveu. */
  nomeAgente?: string;
  /** Plano de fundo escolhido na engrenagem. */
  fundo?: string;
  /** Textura discreta ligada ou desligada. */
  textura?: boolean;
  /** O anúncio que trouxe o lead: aparece citado na primeira mensagem dele, como no WhatsApp. */
  atribuicaoAnuncio?: AtribuicaoAnuncio | null;
}

/**
 * O anúncio citado dentro do balão da primeira mensagem do cliente — é ali que ele
 * aconteceu, e é assim que o WhatsApp mostra: capa, rede, título, texto e o link para
 * a peça. Sem "Desempenho": custo e retorno são da ficha da oportunidade.
 * Pedido dele em 17/10/2026, no lugar da faixa que ficava no topo da conversa.
 */
const AnuncioNaMensagem: React.FC<{ atribuicao: AtribuicaoAnuncio }> = ({ atribuicao }) => {
  const capa = capaDoAnuncio(atribuicao);
  const link = linkDoAnuncio(atribuicao);
  const nome = nomeDoAnuncio(atribuicao);
  const texto = textoDoAnuncio(atribuicao);
  return (
    <div className={`ch-anuncio${capa ? '' : ' is-sem-capa'}`}>
      {capa && (link
        ? <a href={link.url} target="_blank" rel="noopener noreferrer" className="ch-anuncio-capa" aria-label={link.rotulo}><img src={capa} alt="" /></a>
        : <span className="ch-anuncio-capa"><img src={capa} alt="" /></span>)}
      <div className="ch-anuncio-tx">
        <span className="ch-anuncio-rotulo">
          {atribuicao.anuncioRede === 'facebook' ? <FaFacebook size={10} /> : atribuicao.anuncioRede === 'instagram' ? <FaInstagram size={10} /> : null}
          {rotuloRedeDoAnuncio(atribuicao)}
        </span>
        <span className="ch-anuncio-titulo">{nome}</span>
        {texto && <span className="ch-anuncio-corpo">{texto}</span>}
        {link && (
          <a className="ch-anuncio-link" href={link.url} target="_blank" rel="noopener noreferrer">
            <LuExternalLink size={12} />
            {link.rotulo}
          </a>
        )}
      </div>
    </div>
  );
};

const StatusEntrega: React.FC<{ status?: Message['status'] }> = ({ status }) => {
  if (status === 'sending') return <LuClock size={13} className="ch-tick pendente" aria-label="Enviando" />;
  if (status === 'failed') return <LuCircleAlert size={13} className="ch-tick falhou" aria-label="Falha ao enviar" />;
  if (status === 'read') return <LuCheckCheck size={14} className="ch-tick" aria-label="Lida" />;
  if (status === 'delivered') return <LuCheckCheck size={14} aria-label="Entregue" />;
  return <LuCheck size={13} aria-label="Enviada" />;
};

const parseDate = (dateString: string) => {
  const [day, month, year] = dateString.split('/');
  return new Date(`${year}-${month}-${day}`);
};

const ehLegendaVazia = (texto: string | undefined, marcadores: string[]) =>
  !texto || marcadores.includes(texto.toLowerCase());

const ConversationWindow: React.FC<ConversationWindowProps> = ({
  messagesByDate,
  conversationId,
  nomeAgente,
  fundo = 'bege',
  textura = true,
  atribuicaoAnuncio,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);
  /* Abrir uma conversa não pode virar MOVIMENTO na tela. Enquanto a thread se
     posiciona no fim — e enquanto as mídias ainda estão chegando e esticando a
     altura — ela fica invisível e colada no rodapé, sem animação nenhuma. Só
     depois de ancorada é que a rolagem suave volta a valer (mensagem nova). */
  const ancoradoRef = useRef(false);
  const [ancorando, setAncorando] = useState(true);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isScrolledUp, setIsScrolledUp] = useState(false);
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const [midiaQuebrada, setMidiaQuebrada] = useState<number[]>([]);

  const handleDownload = async () => {
    if (!selectedImage) return;
    try {
      const response = await fetch(selectedImage);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `imagem-${Date.now()}.jpg`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Erro ao baixar imagem:', err);
      window.open(selectedImage, '_blank');
    }
  };

  /* Rola a própria lista, não `scrollIntoView` na âncora: o `block: 'end'`
     parava 15px antes do fim por causa do respiro do contêiner. */
  const scrollToBottom = (smooth = false) => {
    const lista = listRef.current;
    if (!lista) return;
    lista.scrollTo({ top: lista.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  };

  const handleScroll = () => {
    if (!listRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 150;
    isAtBottomRef.current = isAtBottom;
    if (isAtBottom) {
      setIsScrolledUp(false);
      setHasNewMessage(false);
    } else {
      setIsScrolledUp(true);
    }
  };

  const forceScrollToBottom = () => {
    scrollToBottom(true);
    setHasNewMessage(false);
  };

  useLayoutEffect(() => {
    ancoradoRef.current = false;
    isAtBottomRef.current = true;
    setAncorando(true);
    setIsScrolledUp(false);
    setHasNewMessage(false);
    setMidiaQuebrada([]);
  }, [conversationId]);

  /* A âncora da abertura. Posiciona no fim ANTES de desenhar e segura a thread
     escondida até a altura parar de mudar: é a imagem que chega depois da
     primeira pintura que empurrava a conversa e disparava a rolagem animada. */
  useLayoutEffect(() => {
    const lista = listRef.current;
    if (!lista || ancoradoRef.current) return;
    if (Object.keys(messagesByDate).length === 0) return;

    const colar = () => { lista.scrollTop = lista.scrollHeight; };
    colar();

    let teto: ReturnType<typeof setTimeout> | undefined;
    let assentamento: ReturnType<typeof setTimeout> | undefined;

    /* Vale depois de revelada também: a fonte da web assenta uns milissegundos
       mais tarde e a lista cresce de novo — sem isto, a conversa abria 73px
       antes do fim. Quem já rolou para cima não é puxado (`isAtBottomRef`). */
    const observador = new ResizeObserver(() => {
      if (!ancoradoRef.current || isAtBottomRef.current) colar();
    });
    observador.observe(lista);
    if (lista.firstElementChild) observador.observe(lista.firstElementChild);

    const liberar = () => {
      if (ancoradoRef.current) return;
      colar();
      ancoradoRef.current = true;
      setAncorando(false);
      if (teto) clearTimeout(teto);
      // o observador ainda segura a conversa no fim enquanto o layout assenta
      assentamento = setTimeout(() => observador.disconnect(), 2500);
    };

    const imagens = Array.from(lista.querySelectorAll('img'));
    imagens.forEach(img => { if (img.complete) img.dataset.ok = '1'; });
    const pendentes: Array<HTMLImageElement | HTMLVideoElement> = imagens.filter(img => !img.complete);
    /* Vídeo e figurinha entraram na conversa em 14/10 e nascem sem altura pelo
       mesmo motivo da imagem: o `preload="metadata"` só diz o tamanho depois. */
    const videos = Array.from(lista.querySelectorAll('video'));
    videos.forEach(v => { if (v.readyState === 0) pendentes.push(v); });
    let faltam = pendentes.length;
    const umaChegou = (e: Event) => {
      (e.currentTarget as HTMLElement).dataset.ok = '1';
      colar();
      if (--faltam <= 0) liberar();
    };
    pendentes.forEach(el => {
      el.addEventListener(el instanceof HTMLVideoElement ? 'loadedmetadata' : 'load', umaChegou, { once: true });
      el.addEventListener('error', umaChegou, { once: true });
    });

    /* Teto: mídia lenta não pode segurar a conversa escondida. Passou disso,
       mostra assim mesmo — e o que carregar depois recola no fim sem animação. */
    teto = setTimeout(liberar, pendentes.length ? 400 : 60);
    if (!pendentes.length) requestAnimationFrame(() => requestAnimationFrame(colar));

    return () => {
      observador.disconnect();
      if (teto) clearTimeout(teto);
      if (assentamento) clearTimeout(assentamento);
      pendentes.forEach(el => {
        el.removeEventListener(el instanceof HTMLVideoElement ? 'loadedmetadata' : 'load', umaChegou);
        el.removeEventListener('error', umaChegou);
      });
    };
  }, [conversationId, messagesByDate]);

  useEffect(() => {
    // A primeira carga da conversa é da âncora acima, e ela não anima nada.
    if (!ancoradoRef.current) return;
    const dates = Object.keys(messagesByDate).sort((a, b) => parseDate(a).getTime() - parseDate(b).getTime());
    let lastMessage: Message | undefined;

    if (dates.length > 0) {
      const messages = messagesByDate[dates[dates.length - 1]];
      if (messages && messages.length > 0) {
        const ordenadas = [...messages].sort((a, b) => a.id - b.id);
        lastMessage = ordenadas[ordenadas.length - 1];
      }
    }

    if (lastMessage?.sender === 'me' || isAtBottomRef.current) {
      scrollToBottom(true);
    } else {
      setHasNewMessage(true);
    }
  }, [messagesByDate]);

  const datasOrdenadas = Object.entries(messagesByDate).sort(
    ([a], [b]) => parseDate(a).getTime() - parseDate(b).getTime()
  );

  /* "A IA passou o atendimento para você": o evento existe na API
     (`EncaminharParaVendedor`), mas NÃO vem na lista de mensagens — então isto
     aqui é derivado do que dá para ver, a primeira vez que a IA falou e em
     seguida quem falou foi o vendedor. Por isso o texto diz o que é
     observável ("você assumiu"), e não o que só o back sabe. */
  const idTransicao = (() => {
    const todas = datasOrdenadas.flatMap(([, msgs]) => [...msgs].sort((a, b) => a.id - b.id));
    for (let i = 1; i < todas.length; i++) {
      if (todas[i].sender === 'me' && todas[i - 1].sender === 'ia') return todas[i].id;
    }
    return null;
  })();

  /* O anúncio é gravado quando a conversa nasce, com a primeira mensagem do cliente —
     então é nela que ele aparece. A conversa vem inteira (sem paginação). */
  const idMensagemDoAnuncio = (() => {
    if (!atribuicaoAnuncio) return null;
    const todas = datasOrdenadas.flatMap(([, msgs]) => [...msgs].sort((a, b) => a.id - b.id));
    return todas.find(m => m.sender === 'other')?.id ?? null;
  })();

  return (
    <div className="ch-thread-casca">
      <div
        className={`ch-thread ${ancorando ? 'is-ancorando' : ''}`}
        ref={listRef}
        onScroll={handleScroll}
        data-fundo={fundo}
        data-textura={textura ? 'on' : 'off'}
      >
        <div className="ch-thread-in">
          {datasOrdenadas.map(([date, messages]) => (
            <React.Fragment key={date}>
              <span className="ch-chip">{date}</span>
              {[...messages]
                .sort((a, b) => a.id - b.id)
                .map(msg => {
                  const quem = msg.sender === 'other' ? 'cliente' : msg.sender === 'ia' ? 'ia' : 'vendedor';
                  const quebrada = midiaQuebrada.includes(msg.id);
                  return (
                    <React.Fragment key={msg.id}>
                    {msg.id === idTransicao && (
                      <span className="ch-chip">
                        <LuHandshake size={13} />
                        Você assumiu o atendimento
                      </span>
                    )}
                    <div className={`ch-linha ${quem === 'cliente' ? '' : 'dir'}`}>
                      <div className={`ch-bolha ${quem}`}>
                        {quem === 'ia' && (
                          <span className="ch-assin">
                            <LuBot size={12} />
                            {nomeAgente ? `${nomeAgente} · IA` : 'IA'}
                          </span>
                        )}

                        {msg.id === idMensagemDoAnuncio && atribuicaoAnuncio && (
                          <AnuncioNaMensagem atribuicao={atribuicaoAnuncio} />
                        )}

                        {msg.type === 'sticker' && msg.mediaUrl && !quebrada ? (
                          <img
                            className="ch-figurinha"
                            src={msg.mediaUrl}
                            alt="Figurinha"
                            onLoad={e => {
                              e.currentTarget.dataset.ok = '1';
                              if (isAtBottomRef.current && listRef.current) {
                                listRef.current.scrollTop = listRef.current.scrollHeight;
                              }
                            }}
                            onError={() => setMidiaQuebrada(atual => [...atual, msg.id])}
                          />
                        ) : msg.type === 'sticker' && quebrada ? (
                          <span className="ch-midia-off">
                            <LuImageOff size={15} />
                            Figurinha indisponível
                          </span>
                        ) : msg.type === 'image' && msg.mediaUrl && !quebrada ? (
                          <>
                            <div className="ch-midia">
                              <img
                                src={msg.mediaUrl}
                                alt="Imagem da conversa"
                                onLoad={e => {
                                  e.currentTarget.dataset.ok = '1';
                                  if (isAtBottomRef.current && listRef.current) {
                                    listRef.current.scrollTop = listRef.current.scrollHeight;
                                  }
                                }}
                                onClick={() => setSelectedImage(msg.mediaUrl!)}
                                onError={() => setMidiaQuebrada(atual => [...atual, msg.id])}
                              />
                            </div>
                            {!ehLegendaVazia(msg.text, ['[imagem]', '[image]']) && <span>{msg.text}</span>}
                          </>
                        ) : msg.type === 'image' && quebrada ? (
                          <span className="ch-midia-off">
                            <LuImageOff size={15} />
                            Imagem indisponível
                          </span>
                        ) : msg.type === 'video' && msg.mediaUrl && !quebrada ? (
                          <>
                            <div className="ch-midia">
                              <video
                                src={msg.mediaUrl}
                                controls
                                preload="metadata"
                                onLoadedMetadata={() => {
                                  if (isAtBottomRef.current && listRef.current) {
                                    listRef.current.scrollTop = listRef.current.scrollHeight;
                                  }
                                }}
                                onError={() => setMidiaQuebrada(atual => [...atual, msg.id])}
                              />
                            </div>
                            {!ehLegendaVazia(msg.text, ['[vídeo]', '[video]', '[reel]']) && <span>{msg.text}</span>}
                          </>
                        ) : msg.type === 'video' && quebrada ? (
                          <span className="ch-midia-off">
                            <LuVideoOff size={15} />
                            Vídeo indisponível
                          </span>
                        ) : msg.type === 'audio' && msg.mediaUrl ? (
                          <>
                            <div className="ch-midia">
                              <AudioPlayer src={msg.mediaUrl} />
                            </div>
                            {!ehLegendaVazia(msg.text, ['[áudio]', '[audio]']) && <span>{msg.text}</span>}
                          </>
                        ) : (msg.type === 'file' || msg.type === 'document') && msg.mediaUrl ? (
                          <a href={msg.mediaUrl} target="_blank" rel="noopener noreferrer" className="ch-arquivo">
                            <LuFile size={15} />
                            <span className="ch-item-txt">{msg.text || 'Arquivo'}</span>
                            <LuDownload size={14} />
                          </a>
                        ) : (
                          <span>{msg.text}</span>
                        )}

                        {/* O conteúdo fica: o vendedor precisa ver o que foi retirado. */}
                        {msg.apagadaEm && (
                          <span
                            className="ch-apagada"
                            title={`Apagada em ${new Date(msg.apagadaEm).toLocaleString('pt-BR')}`}
                          >
                            <LuBan size={12} />
                            {msg.sender === 'other' ? 'Apagada pelo cliente' : 'Apagada para todos'}
                          </span>
                        )}

                        <span className="ch-meta">
                          {msg.time}
                          {msg.sender !== 'other' && <StatusEntrega status={msg.status} />}
                        </span>

                        {msg.reacao && <span className="ch-reacao">{msg.reacao}</span>}
                      </div>
                    </div>
                    </React.Fragment>
                  );
                })}
            </React.Fragment>
          ))}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {isScrolledUp && (
        <button className="ch-descer" onClick={forceScrollToBottom} title="Ir para a última mensagem">
          <LuChevronDown size={18} />
          {hasNewMessage && <span className="ch-descer-dot" />}
        </button>
      )}

      {selectedImage && (
        <div className="image-modal-overlay" onClick={() => setSelectedImage(null)}>
          <div className="image-modal-content" onClick={e => e.stopPropagation()}>
            <header className="image-modal-header">
              <h3>
                <LuImage size={16} style={{ marginRight: 8 }} />
                Visualizar imagem
              </h3>
              <button className="image-modal-close" onClick={() => setSelectedImage(null)}>
                <LuX size={16} />
              </button>
            </header>
            <div className="image-modal-body">
              <img src={selectedImage} alt="Visualização em tela cheia" />
            </div>
            <footer className="image-modal-footer">
              <button className="download-btn" onClick={handleDownload}>
                <LuDownload size={15} style={{ marginRight: 8 }} />
                Baixar imagem
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConversationWindow;
